export const HERO_CHAT_SHELL_ID = "ai-triage-hero-shell";
export const HERO_CHAT_OPEN_SCROLL_BEHAVIOR = "smooth";
export const HERO_CHAT_OPEN_SCROLL_ORIGIN_PX = 0;
export const HERO_CHAT_OPEN_SCROLL_CENTER_DIVISOR = 2;

/**
 * @typedef {{
 *   getBoundingClientRect: () => {
 *     readonly top: number,
 *     readonly height: number,
 *   }
 * }} HeroChatShell
 */

/**
 * @param {Document} [root]
 * @returns {HTMLElement | null}
 */
export function heroChatShellElement(root = document) {
  return root.getElementById(HERO_CHAT_SHELL_ID);
}

/**
 * Leftover viewport space split equally above and below the shell.
 * When the shell is taller than the viewport the inset is the origin so
 * the tab strip stays at the top of the viewport instead of scrolling away.
 *
 * @param {number} leftoverViewportPx
 * @returns {number}
 */
export function heroChatOpenCenterInsetPx(leftoverViewportPx) {
  const centeredInsetPx =
    leftoverViewportPx / HERO_CHAT_OPEN_SCROLL_CENTER_DIVISOR;

  return Math.max(HERO_CHAT_OPEN_SCROLL_ORIGIN_PX, centeredInsetPx);
}

/**
 * @param {HeroChatShell | null} shell
 * @param {{ currentScrollY?: number, viewportHeight?: number }} [options]
 * @returns {number | null}
 */
export function windowScrollTopForCenteredHeroShell(
  shell,
  { currentScrollY = HERO_CHAT_OPEN_SCROLL_ORIGIN_PX, viewportHeight = 0 } = {}
) {
  if (!shell) {
    return null;
  }

  const bounds = shell.getBoundingClientRect();
  const leftoverViewportPx = viewportHeight - bounds.height;
  const centerInsetPx = heroChatOpenCenterInsetPx(leftoverViewportPx);

  return currentScrollY + bounds.top - centerInsetPx;
}

/**
 * @param {{
 *   getShell?: () => HeroChatShell | null,
 *   getScrollY?: () => number,
 *   getViewportHeight?: () => number,
 *   scrollWindowTo?: (options: ScrollToOptions) => void,
 *   behavior?: ScrollBehavior,
 * }} [options]
 * @returns {boolean}
 */
export function scrollWindowToOpenHeroPanel({
  getShell = heroChatShellElement,
  getScrollY = () => window.scrollY,
  getViewportHeight = () => window.innerHeight,
  scrollWindowTo = (options) => window.scrollTo(options),
  behavior = HERO_CHAT_OPEN_SCROLL_BEHAVIOR,
} = {}) {
  const shell = getShell();
  if (!shell) {
    return false;
  }

  const top = windowScrollTopForCenteredHeroShell(shell, {
    currentScrollY: getScrollY(),
    viewportHeight: getViewportHeight(),
  });
  if (top == null) {
    return false;
  }

  scrollWindowTo({
    top: Math.max(HERO_CHAT_OPEN_SCROLL_ORIGIN_PX, top),
    left: HERO_CHAT_OPEN_SCROLL_ORIGIN_PX,
    behavior,
  });
  return true;
}
